import React, { useState, useEffect } from 'react';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Box,
  Stack,
  FormControl,
  InputLabel
} from '@mui/material';
import { 
  Psychology as BrainIcon,
  Person as UserIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowLeftIcon,
  ArrowForward as ArrowRightIcon,
  Storage as DatabaseIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ProceduralMemoryBox from '../Boxes/Memories/ProceduralMemoryBox';
import SemanticMemoryBox from '../Boxes/Memories/SemanticMemoryBox';
import WorkingMemoryBox from '../Boxes/Memories/WorkingMemoryBox';
import EpisodicMemoryBox from '../Boxes/Memories/EpisodicMemoryBox';
import IDBox from '../Boxes/IDBox';
import AgentNode from '../../models/Nodes/AgentNode';
import SemanticMemory from '../../models/Memories/SemanticMemory';
import ProceduralMemory from '../../models/Memories/ProceduralMemory';
import { useEnvironment } from '../../context/EnvironmentContext';
import WorkflowService from '../../services/WorkflowService';
import { AVAILABLE_MODELS, DEFAULT_MODEL, DEFAULT_TEMPERATURE } from '../../config/models';
import PineconeConnection from '../../models/Connections/PineconeConnection';
import AWSConnection from '../../models/Connections/AWSConnection';
import MongoDBConnection from '../../models/Connections/MongoDBConnection';

const getCostColor = (cost: string) => {
  switch (cost) {
    case 'Very High': return 'error';
    case 'High': return 'warning';
    case 'Medium': return 'primary';
    case 'Low': return 'success';
    case 'Very Low': return 'info';
    default: return 'default';
  }
};

interface AgentEditWizardModalProps {
  open: boolean;
  onClose: () => void;
  agent: AgentNode | null;
  onUpdate: (agent: AgentNode) => void;
  allNodes?: any[];
  getEdges?: () => any[];
  setEdges?: (edges: any[]) => void;
  workflowInputs: Record<string, any>;
  setWorkflowInputs?: (inputs: Record<string, any>) => void;
}

// Utility to ensure all connections are class instances
function ensureConnectionInstance(conn: any) {
  if (conn instanceof PineconeConnection || conn instanceof AWSConnection || conn instanceof MongoDBConnection) {
    return conn;
  }
  if (conn.type === 'pinecone') return new PineconeConnection(conn);
  if (conn.type === 'aws') return new AWSConnection(conn);
  if (conn.type === 'mongodb_json') return new MongoDBConnection(conn);
  throw new Error('Unknown connection type');
}

const AgentEditWizardModal: React.FC<AgentEditWizardModalProps> = ({ 
  open, 
  onClose, 
  agent,
  onUpdate,
  allNodes = [],
  getEdges,
  setEdges,
  workflowInputs = {},
  setWorkflowInputs = () => {}
}) => {
  const { environment } = useEnvironment();
  const { enqueueSnackbar } = useSnackbar();
  const [currentStep, setCurrentStep] = useState(0);
  const [currentAgent, setCurrentAgent] = useState<AgentNode | null>(null);
  const [loading, setLoading] = useState(false);
  const [requiredParameters, setRequiredParameters] = useState<string[]>([]);
  const [newParameter, setNewParameter] = useState('');
  const [availableAgents, setAvailableAgents] = useState<AgentNode[]>([]);
  const [selectedDependencies, setSelectedDependencies] = useState<string[]>([]);
  const [activeMemoryTab, setActiveMemoryTab] = useState(0);
  const [model, setModel] = useState<string>(DEFAULT_MODEL);
  const [modelTemperature, setModelTemperature] = useState<number>(DEFAULT_TEMPERATURE);

  const wizardSteps = [
    {
      title: 'Agent Info',
      description: 'Basic agent information'
    },
    {
      title: 'Dependencies',
      description: 'Configure agent dependencies'
    },
    {
      title: 'Model Config',
      description: 'AI model configuration'
    },
    {
      title: 'Memories',
      description: 'Memory systems configuration'
    },
    {
      title: 'Review',
      description: 'Review and save changes'
    }
  ];

  useEffect(() => {
    if (open) {
      loadAgentDetails();
      fetchAvailableAgents();
    }
  }, [open, agent, environment]);

  const loadAgentDetails = async () => {
    if (!agent) return;

    try {
      setLoading(true);
      const workflowService = new WorkflowService(environment);
      const response = await workflowService.getAgentDetails(agent.id);
      const agentData = response.data;

      const loadedAgent = new AgentNode(
        agentData._id,
        SemanticMemory.fromJson(agentData.semantic_memory_args),
        ProceduralMemory.fromJson(agentData.procedural_memory_args),
        'agent_template',
        null,
        null,
        agentData.agent_dependencies || [],
        agentData.parent_agent_id,
        agentData.required_parameters || [],
        agentData.agent_dependencies || [],
        agentData.agent_template_id,
        agentData.uuid // Preserve existing UUID
      );
      loadedAgent.isEnabled = agentData.is_enabled ?? true;

      setCurrentAgent(loadedAgent);
      setRequiredParameters(agentData.required_parameters || []);
      setSelectedDependencies(agentData.agent_dependencies || []);
      setModel(agentData.procedural_memory_args?.model || DEFAULT_MODEL);
      setModelTemperature(agentData.procedural_memory_args?.model_temp || DEFAULT_TEMPERATURE);
    } catch (error) {
      enqueueSnackbar('Failed to load agent details', { variant: 'error' });
      console.error('Error loading agent details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableAgents = async () => {
    try {
      const workflowService = new WorkflowService(environment);
      const response = await workflowService.listAgents();
      const agentsData = Array.isArray(response.data?.agents) ? response.data.agents : [];
      
      const filteredAgents = agentsData.filter(a => a._id !== agent?.id).map(agentData => {
        return new AgentNode(
          agentData._id,
          new SemanticMemory(0.1, []),
          new ProceduralMemory(),
          'agent_template',
          null,
          null,
          [],
          agentData.parent_agent_id,
          agentData.required_parameters || [],
          agentData.agent_dependencies || [],
          agentData.agent_template_id,
          agentData.uuid // Preserve UUID
        );
      });
      
      setAvailableAgents(filteredAgents);
    } catch (error) {
      enqueueSnackbar('Failed to fetch available agents', { variant: 'error' });
      console.error('Error fetching agents:', error);
    }
  };

  const handleProceduralMemoryUpdate = (memory: ProceduralMemory) => {
    if (!currentAgent) return;

    const updatedAgent = new AgentNode(
      currentAgent.id,
      currentAgent.semanticMemory,
      memory,
      'agent_template',
      null,
      null,
      selectedDependencies,
      currentAgent.parentAgentId,
      requiredParameters,
      selectedDependencies,
      currentAgent.agent_template_id,
      currentAgent.uuid // Preserve UUID
    );
    updatedAgent.isEnabled = currentAgent.isEnabled;
    setCurrentAgent(updatedAgent);
  };

  const handleSemanticMemoryUpdate = (memory: SemanticMemory) => {
    if (!currentAgent) return;

    const updatedAgent = new AgentNode(
      currentAgent.id,
      memory,
      currentAgent.proceduralMemory,
      'agent_template',
      null,
      null,
      selectedDependencies,
      currentAgent.parentAgentId,
      requiredParameters,
      selectedDependencies,
      currentAgent.agent_template_id,
      currentAgent.uuid // Preserve UUID
    );
    updatedAgent.isEnabled = currentAgent.isEnabled;
    setCurrentAgent(updatedAgent);
  };

  const handleAddParameter = () => {
    if (newParameter.trim() && !requiredParameters.includes(newParameter.trim())) {
      setRequiredParameters(prev => [...prev, newParameter.trim()]);
      setNewParameter('');
    }
  };

  const handleRemoveParameter = (parameter: string) => {
    setRequiredParameters(prev => prev.filter(p => p !== parameter));
  };

  const handleDependencyChange = (agentId: string, checked: boolean) => {
    if (checked) {
      setSelectedDependencies(prev => [...prev, agentId]);
    } else {
      setSelectedDependencies(prev => prev.filter(id => id !== agentId));
    }
  };

  const handleNext = () => {
    if (currentStep < wizardSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async () => {
    if (!currentAgent) {
      enqueueSnackbar('No agent to update', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      const workflowService = new WorkflowService(environment);
      
      // Update the agent
      const response = await workflowService.updateAgent(currentAgent.id, currentAgent);
      
      if (response.success) {
        enqueueSnackbar('Agent updated successfully', { variant: 'success' });
        onUpdate(currentAgent);
        onClose();
      } else {
        enqueueSnackbar('Failed to update agent', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Error updating agent', { variant: 'error' });
      console.error('Error updating agent:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderMemoryContent = () => {
    if (!currentAgent) return null;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Memory Configuration</Typography>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant={activeMemoryTab === 0 ? 'contained' : 'outlined'}
                onClick={() => setActiveMemoryTab(0)}
                startIcon={<BrainIcon />}
              >
                Procedural
              </Button>
              <Button
                variant={activeMemoryTab === 1 ? 'contained' : 'outlined'}
                onClick={() => setActiveMemoryTab(1)}
                startIcon={<BrainIcon />}
              >
                Semantic
              </Button>
              <Button
                variant={activeMemoryTab === 2 ? 'contained' : 'outlined'}
                onClick={() => setActiveMemoryTab(2)}
                startIcon={<BrainIcon />}
              >
                Working
              </Button>
              <Button
                variant={activeMemoryTab === 3 ? 'contained' : 'outlined'}
                onClick={() => setActiveMemoryTab(3)}
                startIcon={<BrainIcon />}
              >
                Episodic
              </Button>
            </Stack>
          </Box>
          
          {activeMemoryTab === 0 && (
            <ProceduralMemoryBox
              memory={currentAgent.proceduralMemory || new ProceduralMemory(DEFAULT_MODEL, DEFAULT_TEMPERATURE, [], {
                learn: { enabled: false, steps: [] },
                retrieve: { enabled: false, steps: [] },
                reason: { enabled: false, steps: [] },
                design: { enabled: false, steps: [] },
                reflect: { enabled: false, steps: [] }
              }, '')}
              onMemoryChange={handleProceduralMemoryUpdate}
              workflowInputs={workflowInputs}
              setWorkflowInputs={setWorkflowInputs}
              nodeType="agent"
            />
          )}
          
          {activeMemoryTab === 1 && (
            <SemanticMemoryBox
              memory={(() => {
                const mem = currentAgent.semanticMemory || new SemanticMemory(0.1, []);
                const safeConnections = mem.connections.map(ensureConnectionInstance);
                return new SemanticMemory(mem.similarityThreshold, safeConnections);
              })()}
              onMemoryChange={handleSemanticMemoryUpdate}
            />
          )}
          
          {activeMemoryTab === 2 && (
            <WorkingMemoryBox />
          )}
          
          {activeMemoryTab === 3 && (
            <EpisodicMemoryBox />
          )}
        </CardContent>
      </Card>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Agent Info
        return (
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Agent Information</Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>Agent ID</Typography>
                    <TextField
                      fullWidth
                      value={currentAgent?.id || ''}
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                    />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>UUID</Typography>
                    <TextField
                      fullWidth
                      value={currentAgent?.uuid || ''}
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>Status</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={currentAgent?.isEnabled || false}
                          onChange={(e) => {
                            if (currentAgent) {
                              const updatedAgent = new AgentNode(
                                currentAgent.id,
                                currentAgent.semanticMemory,
                                currentAgent.proceduralMemory,
                                currentAgent.type,
                                currentAgent.childData,
                                currentAgent.loopElementsKey,
                                currentAgent.dependencies,
                                currentAgent.parentAgentId,
                                requiredParameters,
                                selectedDependencies,
                                currentAgent.agent_template_id,
                                currentAgent.uuid
                              );
                              updatedAgent.isEnabled = e.target.checked;
                              setCurrentAgent(updatedAgent);
                            }
                          }}
                        />
                      }
                      label={currentAgent?.isEnabled ? 'Enabled' : 'Disabled'}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Required Parameters</Typography>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      value={newParameter}
                      onChange={(e) => setNewParameter(e.target.value)}
                      placeholder="Parameter name"
                      size="small"
                      sx={{ flexGrow: 1 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddParameter}
                      disabled={!newParameter.trim() || requiredParameters.includes(newParameter.trim())}
                    >
                      Add
                    </Button>
                  </Stack>
                  
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {requiredParameters.map((param) => (
                      <Chip
                        key={param}
                        label={param}
                        onDelete={() => handleRemoveParameter(param)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        );

      case 1: // Dependencies
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Agent Dependencies</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select other agents that this agent depends on:
              </Typography>
              <Stack spacing={1}>
                {availableAgents.map((depAgent) => (
                  <FormControlLabel
                    key={depAgent.id}
                    control={
                      <Switch
                        checked={selectedDependencies.includes(depAgent.id)}
                        onChange={(e) => handleDependencyChange(depAgent.id, e.target.checked)}
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip 
                          label="Agent" 
                          size="small"
                          color="primary"
                        />
                        <Typography variant="body2">{depAgent.id}</Typography>
                      </Stack>
                    }
                  />
                ))}
                {availableAgents.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No other agents available for dependencies.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        );

      case 2: // Model Config
        return (
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Model Configuration</Typography>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth>
                      <InputLabel>Model</InputLabel>
                      <Select
                        value={model}
                        onChange={(e) => setModel(e.target.value)}
                        label="Model"
                      >
                        {AVAILABLE_MODELS.map((modelOption) => (
                          <MenuItem key={modelOption.name} value={modelOption.name}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2">{modelOption.name}</Typography>
                              <Chip 
                                label={modelOption.cost} 
                                size="small"
                                color={getCostColor(modelOption.cost)}
                              />
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label="Temperature"
                      type="number"
                      value={modelTemperature}
                      onChange={(e) => setModelTemperature(parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, max: 1, step: 0.01 }}
                      helperText="Controls randomness (0.0 = deterministic, 1.0 = very random)"
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        );

      case 3: // Memories
        return renderMemoryContent();

      case 4: // Review
        return (
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Configuration Summary</Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="body2" fontWeight="bold">Agent ID:</Typography>
                    <Typography variant="body2">{currentAgent?.id || 'Not set'}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="body2" fontWeight="bold">Status:</Typography>
                    <Chip 
                      label={currentAgent?.isEnabled ? 'Enabled' : 'Disabled'} 
                      color={currentAgent?.isEnabled ? 'success' : 'error'}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="body2" fontWeight="bold">Model:</Typography>
                    <Typography variant="body2">{model}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="body2" fontWeight="bold">Temperature:</Typography>
                    <Typography variant="body2">{modelTemperature}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="body2" fontWeight="bold">Parameters:</Typography>
                    <Typography variant="body2">{requiredParameters.length} required</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="body2" fontWeight="bold">Dependencies:</Typography>
                    <Typography variant="body2">{selectedDependencies.length} agents</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2
      }}>
        <Box sx={{ 
          width: 32, 
          height: 32, 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'white'
        }}>
          <UserIcon />
        </Box>
        <Typography variant="h5" fontWeight="bold">
          Edit Agent Wizard
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ py: 3 }}>
        <Stepper 
          activeStep={currentStep} 
          alternativeLabel
          sx={{ mb: 4 }}
        >
          {wizardSteps.map((step, index) => (
            <Step 
              key={step.title} 
              completed={index < currentStep}
              onClick={() => setCurrentStep(index)}
              sx={{ cursor: 'pointer' }}
            >
              <StepLabel>{step.title}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box sx={{ minHeight: 400 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            renderStepContent()
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          onClick={handlePrev}
          disabled={currentStep === 0}
          startIcon={<ArrowLeftIcon />}
          variant="outlined"
        >
          Previous
        </Button>
        
        <Box sx={{ flexGrow: 1 }} />
        
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        
        {currentStep < wizardSteps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowRightIcon />}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleFinish}
            startIcon={<CheckCircleIcon />}
            color="success"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AgentEditWizardModal;